import { useMemo } from 'react';
import { VariantStyle } from '../../../components/BundleSettings/types';
import { NavSections, SectionTitleButton } from './SectionsComponents';
import { Sections } from './Sections';
import { scrollToSection } from './utils';

interface CreateBundleWithSectionsProps {
  bundle: Bundle;
  sellingPlan?: string | number;
  onCardClick: (product) => void;
  addProduct: (
    sectionId: string,
    product: GlobalVariant | GlobalProduct,
  ) => void;
  removeProduct: (
    sectionId: string,
    product: GlobalVariant | GlobalProduct,
  ) => void;
  selectedProducts: Record<string, number>;
  selectedProductsBySection: Record<string, Record<string, number>>;
  setSelectedProducts: (
    sectionId: string,
    newSelectedProducts: Record<string, number>,
  ) => void;
  canAddProducts: boolean;
  BaBProductsToCartFlag: boolean;
  initialSelectedProducts: Record<string, number>;
  hideBaBSoldOutOptions: boolean;
  ignoreBaBOutOfStockAllowance: boolean;
  productsInSections: BabSection[];
  discount?: {
    type: string;
    value: number;
  };
  size: number;
  filteredProducts: GlobalVariant[] | GlobalProduct[];
}

export const CreateBundleWithSections = ({
  bundle,
  sellingPlan,
  onCardClick,
  addProduct,
  removeProduct,
  selectedProducts,
  selectedProductsBySection,
  setSelectedProducts,
  canAddProducts,
  BaBProductsToCartFlag,
  initialSelectedProducts,
  hideBaBSoldOutOptions,
  ignoreBaBOutOfStockAllowance,
  productsInSections,
  discount,
  size,
  filteredProducts = [],
}: CreateBundleWithSectionsProps) => {
  const isClassicBab = bundle?.Type === 'buildABox';
  const isVariantCollapsed = bundle?.VariantStyle === VariantStyle.collapse;
  const hasSections = bundle?.Sections?.length > 1;
  const sectionsMenu = useMemo(
    () =>
      productsInSections?.map(({ name, id }: BabSection) => (
        <SectionTitleButton
          id={`button-${id}`}
          key={id}
          onClick={() => scrollToSection(id)}
          aria-controls={`section-${id}`}
        >
          {name}
        </SectionTitleButton>
      )),
    [productsInSections],
  );

  const objectsInSections = useMemo(
    () =>
      productsInSections?.map((section) => (
        <Sections
          hasSections={hasSections}
          isClassicBab={isClassicBab}
          key={`section-${section.id}`}
          section={section}
          sellingPlan={sellingPlan}
          onCardClick={onCardClick}
          addProduct={addProduct}
          removeProduct={removeProduct}
          selectedProducts={selectedProducts}
          selectedProductsBySection={selectedProductsBySection[section.id]}
          setSelectedProducts={setSelectedProducts}
          canAddProducts={canAddProducts}
          BaBProductsToCartFlag={BaBProductsToCartFlag}
          initialSelectedProducts={initialSelectedProducts}
          hideBaBSoldOutOptions={hideBaBSoldOutOptions}
          ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
          discount={discount}
          isVariantCollapsed={isVariantCollapsed}
          size={size}
          filteredProducts={filteredProducts}
        />
      )),
    [
      productsInSections,
      hasSections,
      sellingPlan,
      onCardClick,
      addProduct,
      removeProduct,
      selectedProducts,
      selectedProductsBySection,
      setSelectedProducts,
      canAddProducts,
      BaBProductsToCartFlag,
      initialSelectedProducts,
      hideBaBSoldOutOptions,
      discount,
      isVariantCollapsed,
      size,
      filteredProducts,
    ],
  );

  return (
    <div className={hasSections ? 'bundle-section-container' : ''}>
      {hasSections && (
        <NavSections className="bundle-section__nav">
          {sectionsMenu}
        </NavSections>
      )}
      {objectsInSections}
    </div>
  );
};
